import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClock,
  faFilePdf,
  faSpinner,
  faTimes,
  faUserCheck,
  faChartLine,
  faForward,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { openTaskTimelinePdfReport } from "../TasksRepot/taskReportService";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskHistoryActionClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  sortTaskHistoryByRecent,
} from "../taskUtils";

const BRAND_LR  = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

/* accent bar colour per action type */
const ACTION_BAR = {
  Created:        "#C04C2A",
  Assigned:       "#3b82f6",
  Forwarded:      "#8b5cf6",
  ProgressChanged:"#f59e0b",
  StatusChanged:  "#10b981",
  Updated:        "#64748b",
};

function getBarColor(actionType) {
  const t = String(actionType || "").toLowerCase();
  if (t.includes("forward"))  return ACTION_BAR.Forwarded;
  if (t.includes("progress")) return ACTION_BAR.ProgressChanged;
  if (t.includes("status") || t.includes("completed")) return ACTION_BAR.StatusChanged;
  if (t.includes("creat"))    return ACTION_BAR.Created;
  if (t.includes("assign"))   return ACTION_BAR.Assigned;
  return ACTION_BAR.Updated;
}

export default function TaskHistoryModal({ task, onClose }) {
  const timeline = useMemo(() => sortTaskHistoryByRecent(task?.history || []), [task]);
  const forwardCount  = getTaskForwardCount(task);
  const forwardLocked = isTaskForwardLocked(task);
  const [creatingPdf, setCreatingPdf] = useState(false);
  const [reportError, setReportError] = useState("");

  const handleOpenPdfReport = async () => {
    setCreatingPdf(true);
    setReportError("");
    try {
      await openTaskTimelinePdfReport(task, timeline, forwardCount, forwardLocked);
    } catch (err) {
      setReportError(err?.message || "PDF report could not be generated.");
    } finally {
      setCreatingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-3">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* shell */}
      <div
        className="relative z-10 flex w-full flex-col overflow-hidden bg-white shadow-2xl"
        style={{ maxWidth: 680, maxHeight: "96dvh", borderRadius: "1.25rem 1.25rem 0 0" }}
      >
        {/* ── Header ── */}
        <div className="relative shrink-0 overflow-hidden px-4 py-4 text-white sm:px-6" style={{ background: BRAND_135 }}>
          <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/5 blur-2xl" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          <div className="pr-8">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
              <FontAwesomeIcon icon={faClock} />
              Task Timeline
            </div>
            <h3 className="mt-1.5 line-clamp-1 text-base font-extrabold tracking-tight text-white sm:text-lg">
              {task.title}
            </h3>
            <p className="mt-0.5 text-[11px] text-orange-200">
              Every handoff, status update, and progress change in one place.
            </p>
          </div>

          {/* thin progress strip */}
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/15">
            <div
              className="h-full rounded-full"
              style={{
                width: `${task.progressPercent || 0}%`,
                background: task.progressPercent >= 100 ? "#6ee7b7" : "rgba(255,255,255,0.65)",
              }}
            />
          </div>
          <div className="mt-0.5 flex items-center justify-between text-[10px] text-orange-200">
            <span>{timeline.length} event{timeline.length !== 1 ? "s" : ""}</span>
            <span>{task.progressPercent || 0}% complete</span>
          </div>
        </div>

        {/* ── Summary strip ── */}
        <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-orange-100 bg-gradient-to-r from-orange-50/60 to-red-50/40 px-4 py-3 sm:grid-cols-4 sm:px-6">
          <MiniCard icon={faUserCheck}  label="Assignee"  value={task.assignedEmployeeName || "Unassigned"} sub={task.assignedEmployeeEmail || "—"} color="#C04C2A" />
          <MiniCard icon={faChartLine}  label="Progress"  value={`${task.progressPercent || 0}%`}           sub={task.status || "Pending"}           color={task.progressPercent >= 100 ? "#10b981" : "#C04C2A"} />
          <MiniCard icon={faForward}    label="Forwards"  value={String(forwardCount)}                      sub={forwardCount ? "handoffs" : "none"}  color="#8b5cf6" />
          <MiniCard icon={faShieldAlt}  label="Fwd Lock"  value={forwardLocked ? "Locked" : "Open"}         sub={forwardLocked ? "step forwarded" : "can forward"} color={forwardLocked ? "#f59e0b" : "#10b981"} />
        </div>

        {/* PDF button row */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-white px-4 py-2 sm:px-6">
          {reportError ? (
            <p className="text-[11px] font-medium text-rose-600">{reportError}</p>
          ) : (
            <p className="text-[11px] text-slate-400">{task.assignedEmployeeEmail || task.assignedUserId || ""}</p>
          )}
          <button
            type="button"
            onClick={handleOpenPdfReport}
            disabled={creatingPdf}
            className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[11px] font-bold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_LR, borderColor: "transparent" }}
          >
            <FontAwesomeIcon icon={creatingPdf ? faSpinner : faFilePdf} spin={creatingPdf} />
            {creatingPdf ? "Preparing…" : "PDF Report"}
          </button>
        </div>

        {/* ── Timeline list ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50/60 px-4 py-3 sm:px-6">
          {timeline.length ? (
            <div className="space-y-2">
              {timeline.map((item, index) => {
                const barColor = getBarColor(item.actionType);
                const showsMovement =
                  item.fromAssignedEmployeeName &&
                  item.toAssignedEmployeeName &&
                  item.fromAssignedEmployeeName !== item.toAssignedEmployeeName;

                return (
                  <div
                    key={item.id || `${item.createdAt}-${index}`}
                    className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                  >
                    {/* left accent bar */}
                    <div className="absolute inset-y-0 left-0 w-[3px] rounded-l-xl" style={{ background: barColor }} />

                    <div className="flex gap-2.5 px-3 py-2.5 pl-4">
                      {/* step number */}
                      <div
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[10px] font-extrabold text-white shadow-sm"
                        style={{ background: barColor }}
                      >
                        {timeline.length - index}
                      </div>

                      <div className="min-w-0 flex-1">
                        {/* top row: badges + date */}
                        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                          <div className="flex flex-wrap items-center gap-1">
                            <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskHistoryActionClasses(item.actionType)}`}>
                              {item.actionLabel}
                            </span>
                            {item.status ? (
                              <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getTaskStatusClasses(item.status)}`}>
                                {item.status}
                              </span>
                            ) : null}
                            <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                              {item.progressPercent || 0}%
                            </span>
                          </div>
                          <span className="shrink-0 text-[10px] text-slate-400">{formatTaskDate(item.createdAt, true)}</span>
                        </div>

                        {/* actor + owner */}
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                          <span className="font-semibold text-slate-800">{item.actorName || "Unknown"}</span>
                          {item.toAssignedEmployeeName ? (
                            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {showsMovement ? "→" : "Owner:"} {item.toAssignedEmployeeName}
                            </span>
                          ) : null}
                        </div>

                        {/* note */}
                        {item.note ? (
                          <p className="mt-1 text-[12px] leading-5 text-slate-600">{item.note}</p>
                        ) : null}

                        {/* movement arrow */}
                        {showsMovement ? (
                          <div
                            className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-semibold"
                            style={{ borderColor: `${barColor}30`, background: `${barColor}08`, color: barColor }}
                          >
                            <span>{item.fromAssignedEmployeeName}</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-[9px] opacity-70" />
                            <span>{item.toAssignedEmployeeName}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed px-6 py-12 text-center text-sm text-slate-400"
              style={{ borderColor: "rgba(192,76,42,0.2)", background: "rgba(192,76,42,0.02)" }}
            >
              No timeline events yet. Events appear after creation, forwarding, or updates.
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex shrink-0 items-center justify-between border-t border-slate-100 bg-white px-4 py-2.5 sm:px-6">
          <span className="text-[11px] text-slate-400">{timeline.length} total event{timeline.length !== 1 ? "s" : ""} · {forwardCount} forward{forwardCount !== 1 ? "s" : ""}</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniCard({ icon, label, value, sub, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center gap-1.5">
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white"
          style={{ background: color }}
        >
          <FontAwesomeIcon icon={icon} className="text-[9px]" />
        </div>
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
      </div>
      <div className="mt-1 truncate text-sm font-extrabold text-slate-800">{value}</div>
      <div className="truncate text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}
